import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import LivretAccessBanner from '../../components/livrets/LivretAccessBanner';
import { ArrowLeft, Eye, Gauge, Scale, Radio, Route as RouteIcon, MoveHorizontal, Settings, ShieldAlert, ClipboardList, AlertTriangle, RotateCcw, Printer, Brain, SlidersHorizontal, Compass, AlertOctagon, Timer, Shirt, Presentation, BookOpen, ArrowRight, Sparkles } from 'lucide-react';
import { ScoreRing } from '../../components/ScoreRing';

const STORAGE_KEY = 'code2roues:epreuves:circulation:v1';

const RAPPELS = [
  { icon: Timer, text: 'Parcours chronométré et noté (~35 min)' },
  { icon: AlertOctagon, text: "1 essai pour la présentation de l'équipement et l'arrêt / départ" },
  { icon: AlertOctagon, text: "1 essai pour l'épreuve en circulation" },
  { icon: AlertOctagon, text: 'En cas d\u2019élimination, l\u2019épreuve est interrompue' },
  { icon: ShieldAlert, text: 'Respecter le code de la route et les consignes de l\u2019examinateur' },
];

const EQUIPEMENT = ['Casque homologué', 'Gants certifiés CE', 'Blouson avec protections', 'Pantalon ou combinaison adaptée', 'Chaussures montantes'];

const COMPETENCES = [
  { id: 1, points: 3, icon: Settings, title: 'Savoir se positionner sur le véhicule, connaître et utiliser les commandes', items: ['Position de conduite', 'Utilisation de la boîte de vitesses et du guidon', 'Utilisation des commandes de freinage, d\u2019embrayage et d\u2019accélération', 'Utilisation des accessoires, tableau de bord et témoins'] },
  { id: 2, points: 3, icon: Eye, title: "Prendre l'information", items: ["Prise d'information vers l'avant et latéralement", "Prise d'information à l'aide des rétroviseurs", 'Prise en compte des angles morts'] },
  { id: 3, points: 3, icon: Gauge, title: 'Adapter son allure aux circonstances', items: ['Adaptation en fonction de la configuration des lieux et des conditions météorologiques', 'Adaptation en fonction des situations de conduite et de la densité de circulation'] },
  { id: 4, points: 3, icon: Scale, title: 'Appliquer la réglementation', items: ['Respect des limitations de vitesse', 'Respect de la signalisation horizontale et verticale', 'Respect des règles de circulation'] },
  { id: 5, points: 3, icon: Radio, title: 'Communiquer avec les autres usagers', items: ['Utilisation des feux indicateurs de direction', 'Compréhension des signaux des autres', 'Utilisation si besoin des autres moyens : gestuelle, feux de détresse, klaxon, appels de phares'] },
  { id: 6, points: 3, icon: RouteIcon, title: 'Partager la chaussée', items: ['Savoir se placer en ligne droite', 'Savoir négocier les virages ou courbes en adoptant la technique de la trajectoire de sécurité', 'Savoir se positionner en changement de direction et franchissement d\u2019intersection', 'Savoir se positionner en s\u2019arrêtant'] },
  { id: 7, points: 3, icon: MoveHorizontal, title: 'Maintenir des espaces de sécurité', items: ['Espacements latéraux', 'Espacements longitudinaux'] },
];

const TRANSVERSALES = [
  { id: 'T1', points: 1, icon: Brain, title: 'Analyse des situations' },
  { id: 'T2', points: 1, icon: SlidersHorizontal, title: 'Adaptation aux situations' },
  { id: 'T3', points: 1, icon: Compass, title: 'Conduite autonome' },
];

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { console.warn('[Circulation] localStorage read failed:', e); }
  return { competences: {}, transversales: {}, equipement: {}, install: { gants: false, casque: false, retros: false, feu: false } };
};

export default function Circulation() {
  const navigate = useNavigate();
  const [state, setState] = useState(loadState);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { console.warn('[Circulation] localStorage write failed:', e); }
  }, [state]);

  const toggleComp = useCallback((cId, sIdx) => {
    setState(s => {
      const cur = { ...(s.competences || {}) };
      const setForC = new Set(cur[cId] || []);
      if (setForC.has(sIdx)) setForC.delete(sIdx); else setForC.add(sIdx);
      cur[cId] = Array.from(setForC);
      return { ...s, competences: cur };
    });
  }, []);

  const toggleTrans = useCallback((tId) => {
    setState(s => ({ ...s, transversales: { ...(s.transversales || {}), [tId]: !s.transversales?.[tId] } }));
  }, []);

  const toggleEquip = useCallback((idx) => {
    setState(s => ({ ...s, equipement: { ...(s.equipement || {}), [idx]: !s.equipement?.[idx] } }));
  }, []);

  const toggleInstall = useCallback((key) => {
    setState(s => ({ ...s, install: { ...(s.install || {}), [key]: !s.install?.[key] } }));
  }, []);

  const reset = () => {
    if (window.confirm('Réinitialiser tes acquis sur cette fiche ?')) setState({ competences: {}, transversales: {}, equipement: {}, install: { gants: false, casque: false, retros: false, feu: false } });
  };

  const totalItems = COMPETENCES.reduce((a, c) => a + c.items.length, 0) + TRANSVERSALES.length;
  const doneItems = COMPETENCES.reduce((a, c) => a + ((state.competences?.[c.id] || []).length), 0) + TRANSVERSALES.filter(t => state.transversales?.[t.id]).length;
  const pct = totalItems ? Math.round((doneItems / totalItems) * 100) : 0;

  return (
    <div className="space-y-5 print:space-y-3" data-testid="circulation-page">
      <Button onClick={() => navigate('/epreuves')} variant="ghost" size="sm" className="-ml-2 print:hidden" data-testid="circulation-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux épreuves
      </Button>

      {/* Accès direct au livret Circulation interactif — visible dès le haut de la page */}
      <LivretAccessBanner
        livretTo="/livret-circulation"
        livretName="Livret Circulation A1/A2 — 7 chapitres à cocher"
        desc="Coche tes acquis en circulation avec ou sans ton moniteur. Sauvegarde locale, pas besoin de compte."
      />

      <section className="mc-card relative overflow-hidden p-5 sm:p-7" data-testid="circulation-hero">
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20" style={{ background: 'hsl(var(--primary))' }} />
        <div className="relative flex items-center gap-4 flex-wrap justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1 inline-flex items-center gap-1">
              <RouteIcon className="w-3 h-3" /> Épreuve en circulation
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Maîtrise • Sécurité • Responsabilité</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">~35 min de conduite · 7 compétences dynamiques + 3 transversales · barème 21/27</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Acquis</div>
              <div className="text-xl font-black" data-testid="circulation-progress">{doneItems}<span className="text-muted-foreground text-sm">/{totalItems}</span></div>
            </div>
            <ScoreRing value={doneItems} total={totalItems} size={68} stroke={7} testid="circulation-score-ring" />
          </div>
        </div>
        <div className="relative h-1.5 bg-border rounded-full mt-4 overflow-hidden">
          <div className="h-full c2r-progress-glow" style={{ width: `${pct}%` }} />
        </div>
      </section>

      {/* Bannière pédagogique — Cours premium + Livret interactif */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 print:hidden" data-testid="circulation-pedago-cta">
        <Link
          to="/cours/premiere-circulation-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-premiere-circulation"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Cours 1
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 1ʳᵉ circulation A2
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Préparation, groupe, radio, méthode d'intersection, bilan et 7 réflexes.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/deuxieme-circulation-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-deuxieme-circulation"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Cours 2 · Nouveau
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 2ᵉ circulation A2
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Observer • Anticiper • Adapter. Regard, indices, espace, autonomie.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/troisieme-circulation-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-troisieme-circulation"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Cours 3 · Nouveau
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 3ᵉ circulation A2
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Décider • Planifier • S'adapter. Vers l'autonomie complète.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/quatrieme-circulation-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-quatrieme-circulation"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Cours 4 · Nouveau
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 4ᵉ circulation A2
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Autonomie • Anticipation • Choix. Proche du niveau examen.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/cinquieme-circulation-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-cinquieme-circulation"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Cours 5 · Nouveau
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 5ᵉ circulation A2
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Consolider • S'adapter. Séquence type examen et auto-débriefing.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/sixieme-circulation-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-sixieme-circulation"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Cours 6 · Final
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 6ᵉ circulation A2
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Valider • Bilan final. 12 h minimales atteintes.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/livret-circulation"
          className="group relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-600/20 via-blue-500/10 to-transparent p-4 sm:p-5 hover:border-cyan-500/60 hover:from-cyan-600/30 transition-all"
          data-testid="cta-livret-circulation"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <BookOpen size={110} strokeWidth={1.2} className="text-cyan-400" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-400 mb-2">
              <BookOpen className="w-3 h-3" /> Livret interactif
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Livret Circulation A1/A2
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              7 chapitres à cocher, PDF téléchargeable, progression sauvegardée.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-cyan-400 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      </section>

      <section className="grid md:grid-cols-3 gap-3" data-testid="circulation-context">
        <div className="mc-card p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2 inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Déroulement</div>
          <p className="text-sm leading-relaxed text-foreground/85">
            La phase de conduite (présentation équipement, installation sur le motocycle, situation arrêt / départ) dure environ 35 min.
            Le parcours comporte globalement <strong>1/3 en agglomération</strong> et <strong>2/3 hors agglomération</strong>, avec une phase guidée et une phase de conduite autonome.
          </p>
        </div>
        <div className="mc-card p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2 inline-flex items-center gap-1"><Shirt className="w-3 h-3" /> Équipement obligatoire</div>
          <ul className="space-y-1.5 text-sm">
            {EQUIPEMENT.map((e, i) => (
              <li key={i}>
                <label className="inline-flex items-start gap-2 cursor-pointer w-full" data-testid={`circ-equipement-${i}`}>
                  <input type="checkbox" checked={!!state.equipement?.[i]} onChange={() => toggleEquip(i)} className="mt-0.5 w-4 h-4 accent-[hsl(var(--primary))] flex-shrink-0" />
                  <span className={`leading-snug ${state.equipement?.[i] ? 'line-through text-muted-foreground' : ''}`}>{e}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-2.5 px-2.5 py-2 rounded-lg border text-[11px] leading-snug flex items-start gap-1.5" style={{ borderColor: 'hsl(var(--primary) / 0.4)', background: 'hsl(var(--primary) / 0.05)' }}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 text-[hsl(var(--primary))]" />
            <span><strong>Équipement non conforme :</strong> examen refusé ou noté à la baisse.</span>
          </div>
        </div>
        <div className="mc-card p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2 inline-flex items-center gap-1"><AlertOctagon className="w-3 h-3" /> Rappels</div>
          <ul className="space-y-1.5 text-sm">
            {RAPPELS.map((r, i) => { const Icon = r.icon; return (<li key={i} className="flex gap-2"><Icon className="w-3.5 h-3.5 flex-shrink-0 text-[hsl(var(--primary))] mt-0.5" /><span className="leading-snug">{r.text}</span></li>); })}
          </ul>
        </div>
      </section>

      {/* Savoir s'équiper et s'installer */}
      <section className="mc-card overflow-hidden" data-testid="circulation-install">
        <div className="px-4 py-2.5 font-black text-sm uppercase tracking-wide" style={{ background: 'hsl(var(--primary))', color: 'white' }}>Savoir s'équiper et s'installer <span className="opacity-80 ml-1 font-semibold normal-case text-xs">(2 points)</span></div>
        <div className="p-4 grid sm:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="font-black mb-1.5">Savoir s'équiper :</div>
            <label className="inline-flex items-start gap-2 cursor-pointer" data-testid="circ-install-equip">
              <input type="checkbox" checked={!!state.install?.gants} onChange={() => toggleInstall('gants')} className="mt-0.5 w-4 h-4 accent-[hsl(var(--primary))]" />
              <span className={state.install?.gants ? 'line-through text-muted-foreground' : ''}>Présenter les EPI : gants, bottes, vêtements couvrants et casque homologué à la bonne taille.</span>
            </label>
          </div>
          <div>
            <div className="font-black mb-1.5">Savoir s'installer :</div>
            <label className="inline-flex items-start gap-2 cursor-pointer" data-testid="circ-install-bike">
              <input type="checkbox" checked={!!state.install?.retros} onChange={() => toggleInstall('retros')} className="mt-0.5 w-4 h-4 accent-[hsl(var(--primary))]" />
              <span className={state.install?.retros ? 'line-through text-muted-foreground' : ''}>Régler les rétroviseurs, allumer et vérifier le feu de croisement, contrôler l'absence de témoin d'alerte au tableau de bord.</span>
            </label>
          </div>
        </div>
      </section>

      {/* 7 compétences dynamiques */}
      <section className="space-y-1" data-testid="circulation-competences">
        <div className="px-4 py-2.5 mc-card font-black text-sm uppercase tracking-wide" style={{ background: 'hsl(var(--primary))', color: 'white' }}>
          Évaluation des compétences dynamiques <span className="opacity-80 ml-1 font-semibold normal-case text-xs">(21 points — 3 par compétence)</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-2">
          {COMPETENCES.map((c) => {
            const Icon = c.icon;
            const done = (state.competences?.[c.id] || []).length;
            const total = c.items.length;
            return (
              <div key={c.id} className="mc-card overflow-hidden" data-testid={`circ-comp-${c.id}`}>
                <div className="px-3.5 py-2 flex items-center gap-2 bg-[hsl(var(--panel))] border-b border-border">
                  <div className="w-7 h-7 rounded-md grid place-items-center font-black text-white text-xs" style={{ background: '#1d4ed8' }}>{c.id}</div>
                  <Icon className="w-4 h-4 text-foreground/70 flex-shrink-0" />
                  <div className="font-black text-[12.5px] leading-tight flex-1">{c.title}</div>
                  <span className="text-[10px] font-bold text-muted-foreground">{c.points} pts</span>
                  <span className="text-[10px] font-black text-[hsl(var(--primary))]">{done}/{total}</span>
                </div>
                <ul className="p-3.5 space-y-2">
                  {c.items.map((item, idx) => {
                    const checked = (state.competences?.[c.id] || []).includes(idx);
                    return (
                      <li key={idx}>
                        <label className="flex items-start gap-2.5 cursor-pointer" data-testid={`circ-comp-${c.id}-item-${idx}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleComp(c.id, idx)} className="mt-0.5 w-4 h-4 accent-[hsl(var(--primary))] flex-shrink-0" />
                          <span className={`text-sm leading-snug ${checked ? 'line-through text-muted-foreground' : ''}`}>{item}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Transversales */}
      <section className="space-y-2" data-testid="circulation-transversales">
        <div className="px-4 py-2.5 mc-card font-black text-sm uppercase tracking-wide" style={{ background: '#b45309', color: 'white' }}>
          Évaluation des compétences transversales <span className="opacity-80 ml-1 font-semibold normal-case text-xs">(3 points)</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {TRANSVERSALES.map((t) => {
            const Icon = t.icon;
            const checked = !!state.transversales?.[t.id];
            return (
              <label key={t.id} className="mc-card p-4 cursor-pointer flex items-start gap-3" data-testid={`circ-trans-${t.id}`}>
                <input type="checkbox" checked={checked} onChange={() => toggleTrans(t.id)} className="mt-1 w-4 h-4 accent-[hsl(var(--primary))]" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: '#b45309', color: 'white' }}><Icon className="w-4 h-4" /></div>
                    <div className="font-black text-[13px] leading-tight">{t.title}</div>
                  </div>
                  <div className="text-[10px] font-bold text-muted-foreground mt-1">{t.points} point</div>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      {/* Évaluation + Erreurs + Éliminations + Barème */}
      <section className="grid md:grid-cols-4 gap-3" data-testid="circulation-eval">
        <div className="mc-card p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-2">Évaluation globale</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2"><span className="w-7 h-7 rounded-full grid place-items-center font-black text-white" style={{ background: '#16a34a' }}>A</span> Exercice correctement réalisé</li>
            <li className="flex items-center gap-2"><span className="w-7 h-7 rounded-full grid place-items-center font-black text-white" style={{ background: '#f59e0b' }}>B</span> Une erreur</li>
            <li className="flex items-center gap-2"><span className="w-7 h-7 rounded-full grid place-items-center font-black text-white" style={{ background: 'hsl(var(--primary))' }}>C</span> Deux erreurs ou une élimination (recalé)</li>
          </ul>
        </div>
        <div className="mc-card p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2">Les erreurs (Note B)</div>
          <p className="text-sm leading-relaxed text-foreground/85">Cône ou piquet déplacé ou renversé, mauvaise maîtrise de l'embrayage, pieds au sol, 3<sup>e</sup> vitesse non enclenchée.</p>
        </div>
        <div className="mc-card p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2">Les éliminations (Note C)</div>
          <p className="text-sm leading-relaxed text-foreground/85">Embrayage non acquis, erreur de parcours, obstacle ou porte non franchi, vitesse minimale non atteinte, distance d'arrêt trop longue, anticipation du freinage d'urgence, exercice non réalisé, chute, renversement ou déplacement de cône lors de l'évitement, sortie de terrain.</p>
        </div>
        <div className="mc-card p-4" style={{ borderColor: '#16a34a' }}>
          <div className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#16a34a' }}>Barème</div>
          <ul className="space-y-1.5 text-sm">
            <li>• Savoir s'équiper / s'installer : <strong>0 à 2 pts</strong></li>
            <li>• 7 compétences dynamiques : <strong>0 à 3 pts</strong> chacune</li>
            <li>• Compétences transversales : <strong>0 à 3 pts</strong></li>
            <li>• Courtoisie envers les autres usagers : <strong>+1 pt</strong></li>
          </ul>
          <div className="mt-3 px-3 py-2 rounded-lg font-black text-center text-base" style={{ background: '#16a34a', color: 'white' }}>21 / 27 points pour réussir</div>
        </div>
      </section>

      <section className="mc-card p-4 text-sm leading-relaxed border-l-4" style={{ borderLeftColor: 'hsl(var(--primary))' }} data-testid="circulation-eliminatoire">
        <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Erreur éliminatoire</div>
        <p className="text-foreground/85">
          Toute action, non-action ou comportement dangereux du candidat où la sécurité dépendrait essentiellement des réactions des autres usagers entraîne une élimination immédiate.
        </p>
      </section>

      <div className="flex flex-wrap gap-2 print:hidden" data-testid="circulation-actions">
        <Button variant="outline" onClick={() => window.print()} className="rounded-full" size="sm"><Printer className="w-4 h-4 mr-1.5" /> Imprimer</Button>
        <Button variant="outline" onClick={reset} className="rounded-full" size="sm" data-testid="circulation-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Réinitialiser</Button>
      </div>

      <p className="text-[11px] text-muted-foreground italic mt-2 leading-relaxed">
        Source : programme officiel de l'épreuve en circulation moto (arrêté du 23 avril 2012, REMC).
      </p>
    </div>
  );
}
